// ---------------------------------------------------------------------------
// Single source of truth for platform-wide constants.
// Import from here — never re-declare these lists in components.
// The BRAND WORDMARK itself lives in src/lib/brand.ts (GIGGONE).
// ---------------------------------------------------------------------------
import { BRAND_NAME, BRAND_SUFFIX, BRAND_FULL_NAME } from '@/lib/brand';


/**
 * VERIFIED BUSINESS ADDRESS — single source of truth.
 *
 * This is the registered mailing address for GIGGONE Marketplace. It is shown
 * to humans (footer, Contact, About) AND emitted as machine-readable
 * schema.org PostalAddress on every page (Organization + LocalBusiness), so
 * search engines, AI crawlers, security reputation vendors and enterprise IT
 * reviewers all resolve the same physical entity.
 *
 * Never retype the address anywhere — import BUSINESS_ADDRESS.
 */

export const BUSINESS_ADDRESS = {
  streetAddress: '15891 Foothill Blvd, Suite 5',
  addressLocality: 'Fontana',
  addressRegion: 'CA',
  postalCode: '92335',
  addressCountry: 'US',
  countryName: 'United States',
};

/** Address as display lines, e.g. for a stacked <address> block. */
export const ADDRESS_LINES: string[] = [
  BUSINESS_ADDRESS.streetAddress,
  `${BUSINESS_ADDRESS.addressLocality}, ${BUSINESS_ADDRESS.addressRegion} ${BUSINESS_ADDRESS.postalCode}`,
  BUSINESS_ADDRESS.countryName,
];

/** Address on one line, e.g. "15891 Foothill Blvd, Suite 5, Fontana, CA 92335, United States". */
export const ADDRESS_ONE_LINE = ADDRESS_LINES.join(', ');

/** schema.org PostalAddress node — used by Organization and LocalBusiness. */
export const postalAddressSchema = () => ({
  '@type': 'PostalAddress',
  streetAddress: BUSINESS_ADDRESS.streetAddress,
  addressLocality: BUSINESS_ADDRESS.addressLocality,
  addressRegion: BUSINESS_ADDRESS.addressRegion,
  postalCode: BUSINESS_ADDRESS.postalCode,
  addressCountry: BUSINESS_ADDRESS.addressCountry,
});

/**
 * BRAND — the wordmark itself lives in src/lib/brand.ts so that a single edit
 * restyles every surface (logo, header, footer, meta titles, Open Graph,
 * JSON-LD, email sender names, legal copy). Never type the brand by hand here.
 */
export const BRAND = {
  name: BRAND_NAME,
  suffix: BRAND_SUFFIX,
  fullName: BRAND_FULL_NAME,
  tagline_en: 'Where construction work finds the right crew.',
  tagline_es: 'Donde el trabajo de construcción encuentra al equipo correcto.',
  /** The ONLY public contact channel. No public support phone number is displayed. */
  email: 'info@giggone.com',
  /** Verified street address on one line. Structured parts: BUSINESS_ADDRESS. */
  address: ADDRESS_ONE_LINE,
  /** Human-readable region served, used in copy where a full address does not fit. */
  region: 'Inland Empire, California',
  /**
   * Official one-line company description. Single source of truth — used in the
   * footer, the About page, the Organization schema and llms.txt. Do not retype
   * this sentence anywhere else; import brandDescription() instead.
   */
  description_en: `${BRAND_NAME} is a marketplace connecting homeowners, general contractors and subcontractors.`,
  description_es: `${BRAND_NAME} es un marketplace que conecta a dueños de casa, contratistas generales y subcontratistas.`,
};


/** Official company description in the active language. */
export const brandDescription = (lang: Lang): string =>
  lang === 'es' ? BRAND.description_es : BRAND.description_en;


/**
 * ───────────────────────────────────────────────────────────────────────────
 * BRAND IMAGES FOR SEARCH + SOCIAL — single source of truth.
 * ───────────────────────────────────────────────────────────────────────────
 * LOGO_IMAGE   square mark, emitted as Organization.logo (ImageObject) so
 *              Google/Bing can resolve a crawlable logo for the brand entity.
 *              Same file the PWA manifest already ships, so there is exactly
 *              one logo asset on the platform.
 * SOCIAL_IMAGE 16:9 share card used for og:image / twitter:image on every
 *              page that does not provide its own. Without it, link previews
 *              and `summary_large_image` cards render blank.
 *
 * Never inline either URL in a component or in index.html's runtime code —
 * import from here so a rebrand is a one-line change.
 */
export const LOGO_IMAGE =
  'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789922279410_1fbf836f.webp';

export const SOCIAL_IMAGE =
  'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1790055887715_891b24ea.jpg';

/** Rendered dimensions of SOCIAL_IMAGE, declared to crawlers via og:image:*. */
export const SOCIAL_IMAGE_SIZE = { width: 1344, height: 768 };



/**
 * ───────────────────────────────────────────────────────────────────────────
 * WHATSAPP SALES / SUPPORT LINE — single source of truth.
 * ───────────────────────────────────────────────────────────────────────────
 * Used by the sitewide floating WhatsApp button (and anywhere else a "message
 * us" affordance is offered). Never retype the number: import SUPPORT_WHATSAPP
 * or call whatsappHref() so the digits, the display format and the prefilled
 * greeting can only ever change in one place.
 *
 * `digits` is the E.164 number WITHOUT the leading "+" — that is exactly the
 * format wa.me expects (https://wa.me/19097296207). `display` is the human
 * format shown in copy and in the aria-label.
 */
export const SUPPORT_WHATSAPP = {
  digits: '19097296207',
  display: '+1 (909) 729-6207',
  /** Prefilled first message, so the conversation opens with real context. */
  greeting_en:
    'Hi, I have a question about GIGGONE memberships, leads, or contractor services.',
  greeting_es:
    'Hola, tengo una pregunta sobre las membresías, los leads o los servicios para contratistas de GIGGONE.',

};

/**
 * Build the wa.me deep link with the prefilled greeting in the active language.
 * wa.me opens the native app on mobile and WhatsApp Web on desktop, so one URL
 * covers every device — no user-agent sniffing required.
 */
export const whatsappHref = (lang: Lang): string =>
  `https://wa.me/${SUPPORT_WHATSAPP.digits}?text=${encodeURIComponent(
    lang === 'es' ? SUPPORT_WHATSAPP.greeting_es : SUPPORT_WHATSAPP.greeting_en
  )}`;



/**
 * PUBLIC TRUST PAGES — Privacy Policy, Terms of Service, Contact Us, About Us.
 * These four pages must be reachable with NO authentication, must be linked in
 * the footer of every page, and must stay crawlable (see public/robots.txt).
 * Single source of truth: the footer, the About page and the sitemap all read
 * this list, so a page can never be linked in one place and missed in another.
 */
export interface TrustPage {
  /** English route path (path() localizes it automatically). */
  to: string;
  /** translation key for the link label */
  key: string;
}

export const TRUST_PAGES: TrustPage[] = [
  { to: '/about', key: 'footer.aboutUs' },
  { to: '/contact', key: 'footer.contactUs' },
  { to: '/privacy', key: 'footer.privacy' },
  { to: '/terms', key: 'footer.terms' },
];



/**
 * FIXED LEAD PRICE — one price for every lead on the marketplace.
 * A lead never costs more or less because of the trade, the project type,
 * the budget, the city or who posted it. $49.99 = 4999 cents.
 * Import LEAD_PRICE anywhere a lead price is shown or charged.
 */
export const LEAD_PRICE = 4999;

/**
 * Subscription plans. Prices/allowances are mirrored from mkt_plans and are the
 * values the hosted checkout links and the payment webhook must agree on.
 *   Basic   $99/mo   ->  5 leads   (monthly, cancel anytime)
 *   Pro     $199/mo  -> 10 leads   (monthly, cancel anytime)
 *   Premium $499/mo  -> 25 leads   (monthly charge, 12-MONTH COMMITMENT)
 *
 * PREMIUM ALLOWANCE CHANGE (25, previously 20): the monthly charge is
 * UNCHANGED at $499. Only the included credit count moved. The same number
 * lives in exactly three places and they must never disagree:
 *   1. mkt_plans.included_leads  (database — what Pricing renders)
 *   2. PLAN_INCLUDED_LEADS below (client display + schema/FAQ copy)
 *   3. PLAN_LEADS in the payment-webhook / checkout-link edge functions
 *      (what a purchase and a renewal actually grant)
 *
 * PREMIUM BILLING — read before changing anything here:
 * PLAN_PRICES.premium is the MONTHLY charge ($499), not a term total. The buyer
 * pays $499 each month for 12 months; they are NOT charged $5,988 upfront.
 * PLAN_TERM_MONTHS.premium = 12 is the length of the COMMITMENT, not the
 * billing interval — every plan bills monthly. The $5,988 total agreement value
 * is always derived with planTermTotal(), never retyped, so the two numbers can
 * never drift apart. Always render a plan price through planBillingSuffix() /
 * planPriceLabel() and pair a committed plan with planTermNote().
 */
export const PLAN_CODES = ['basic', 'pro', 'premium'] as const;
export type PlanCode = (typeof PLAN_CODES)[number];

export const PLAN_INCLUDED_LEADS: Record<string, number> = {
  basic: 5,
  pro: 10,
  /** Raised from 20 to 25. The $499 monthly charge did not change. */
  premium: 25,
};

/** MONTHLY charge for each plan, in cents. Premium is $499/mo — not a term total. */
export const PLAN_PRICES: Record<string, number> = {
  basic: 9900,
  pro: 19900,
  /** $499 charged every month; requires a 12-month commitment (see PLAN_TERM_MONTHS). */
  premium: 49900,
};

/**
 * ONE-OFF PRODUCT — the $29 single lead credit.
 *
 * Not a plan: it never renews and never creates a subscription. It adds ONE
 * spendable credit to the balance, exactly like a membership credit, and the
 * contractor chooses which open project to spend it on.
 *
 * `code` is the value every purchase surface must send to the checkout-link
 * edge function, which resolves it to GHL_SINGLE_CHECKOUT_URL.
 */
export const SINGLE_LEAD_PRODUCT = {
  code: 'single' as const,
  price: LEAD_PRICE,
  credits: 1,
  recurring: false,
  /** Environment variable holding the hosted checkout URL for this product. */
  envName: 'GHL_SINGLE_CHECKOUT_URL',
};


/**
 * Minimum COMMITMENT length in months. This is not the billing interval —
 * every plan is charged monthly. 1 means month-to-month / cancel anytime.
 */
export const PLAN_TERM_MONTHS: Record<string, number> = {
  basic: 1,
  pro: 1,
  premium: 12,
};

export const planTermMonths = (code?: string | null): number =>
  (code && PLAN_TERM_MONTHS[code]) || 1;

/** Total value of a committed plan over its full term, in cents (e.g. $499 x 12 = $5,988). */
export const planTermTotal = (code: string | null | undefined, monthlyCents: number): number =>
  monthlyCents * planTermMonths(code);

/** Billing interval suffix. Every plan bills monthly, so this is always "/mo". */
export const planBillingSuffix = (_code: string | null | undefined, lang: Lang): string =>
  lang === 'es' ? '/mes' : '/mo';

/** Full price label, e.g. "$199/mo" or "$499/mo". */
export const planPriceLabel = (cents: number, code: string | null | undefined, lang: Lang): string =>
  `${money(cents)}${planBillingSuffix(code, lang)}`;
/**
 * COMMITMENT DISCLOSURE for any plan with a minimum term.
 *
 * PREMIUM CONTRADICTION FIX: Premium may NEVER be described as "cancel
 * anytime". The exact, approved disclosure is generated here (derived from
 * PLAN_PRICES + PLAN_TERM_MONTHS, never retyped) and is rendered by the plan
 * picker, the checkout modal and the pricing cards, so the monthly charge, the
 * $5,988 total agreement value, the "not charged upfront" clarification and
 * the credit-expiry rule can only ever exist in ONE place.
 *
 * Month-to-month plans (Basic, Pro) return an empty string — only they are
 * allowed to show `cta.cancelAnytime`, and planIsCancelAnytime() below is the
 * single guard every surface must use before rendering that phrase.
 */
export const planTermNote = (code: string | null | undefined, cents: number, lang: Lang): string => {
  const months = planTermMonths(code);
  if (months <= 1) return '';
  const total = money(planTermTotal(code, cents));
  return lang === 'es'
    ? `${money(cents)} al mes durante ${months} pagos mensuales. Valor total del acuerdo: ${total}. No se cobra el monto completo por adelantado. Los créditos de lead no usados expiran al final de cada ciclo de facturación y no se acumulan.`
    : `${money(cents)} per month for ${months} monthly payments. Total agreement value: ${total}. The full amount is not charged upfront. Unused lead credits expire at the end of each billing cycle and do not roll over.`;
};

/**
 * TRUE only for genuinely month-to-month plans. Every surface that wants to
 * print "cancel anytime" MUST gate on this, so a committed plan (Premium)
 * cannot contradict its own 12-month agreement anywhere on the site.
 */
export const planIsCancelAnytime = (code: string | null | undefined): boolean =>
  planTermMonths(code) <= 1;


/**
 * Static plan display names. The authoritative plan rows live in `mkt_plans`
 * (and the Pricing page renders those), but lightweight surfaces such as the
 * floating CTA plan picker need a label without waiting on a database round
 * trip. Prices/allowances are still read from PLAN_PRICES /
 * PLAN_INCLUDED_LEADS above so there is only ever one copy of those numbers.
 */
export const PLAN_NAMES: Record<string, { en: string; es: string }> = {
  basic: { en: 'Basic', es: 'Básico' },
  pro: { en: 'Pro', es: 'Pro' },
  premium: { en: 'Premium', es: 'Premium' },
};

export const planLabel = (code: string, lang: Lang): string =>
  (PLAN_NAMES[code] && PLAN_NAMES[code][lang]) || code;

/**
 * ───────────────────────────────────────────────────────────────────────────
 * BASIC INTRO OFFER — single source of truth for every surface that shows it.
 * ───────────────────────────────────────────────────────────────────────────
 * Month 1: $50 with 3 lead credits. Month 2 onward: the normal Basic price
 * (PLAN_PRICES.basic) with the normal allowance (PLAN_INCLUDED_LEADS.basic).
 *
 * The code is a real row in mkt_coupons (intro_credits = 3). The server is the
 * authority: payment-webhook grants intro_credits for the discounted first
 * payment and the full allowance on every $99 renewal. GoHighLevel charges the
 * card, so the SAME code must exist in GHL as a ONE-TIME coupon on Basic.
 */
export const BASIC_INTRO_OFFER = {
  plan: 'basic' as const,
  coupon: 'BASIC50',
  firstMonthCents: 5000,
  firstMonthCredits: 3,
};

/** "First month $50 (3 leads), then $99/mo with 5 leads." */
export const introOfferLine = (lang: Lang): string => {
  const o = BASIC_INTRO_OFFER;
  const later = money(PLAN_PRICES[o.plan]);
  const leads = PLAN_INCLUDED_LEADS[o.plan];
  return lang === 'es'
    ? `Primer mes ${money(o.firstMonthCents)} con ${o.firstMonthCredits} leads. Desde el mes 2: ${later}/mes con ${leads} leads al mes.`
    : `First month ${money(o.firstMonthCents)} with ${o.firstMonthCredits} leads. From month 2: ${later}/mo with ${leads} leads per month.`;
};

/**
 * Upgrade pitch for each paid tier — rendered by the dashboard / membership
 * upgrade banners. Prices and lead counts are NOT retyped: they come from
 * PLAN_PRICES / PLAN_INCLUDED_LEADS.
 *
 * HONESTY RULE (2026-10-14): only benefits that exist today. "Priority access to
 * new opportunities" and "Priority placement" were removed — neither is built.
 * Re-add them only after member-first job alerts / placement actually ship.
 */
export const UPGRADE_PERKS: Record<'pro' | 'premium', { en: string[]; es: string[]; badge?: { en: string; es: string } }> = {
  pro: {
    en: [
      `${PLAN_INCLUDED_LEADS.pro / PLAN_INCLUDED_LEADS.basic}x the monthly credits of Basic`,
      'Lower cost per lead than single purchases',
      'Cancel anytime',
    ],
    es: [
      `${PLAN_INCLUDED_LEADS.pro / PLAN_INCLUDED_LEADS.basic}x los créditos mensuales de Básico`,
      'Menor costo por lead que la compra individual',
      'Cancele cuando quiera',
    ],
  },
  premium: {
    en: ['FREE website included', `${PLAN_INCLUDED_LEADS.premium} credits every month`, 'Lowest cost per lead at volume'],
    es: ['Sitio web GRATIS incluido', `${PLAN_INCLUDED_LEADS.premium} créditos cada mes`, 'Menor costo por lead en volumen'],
    badge: { en: 'Limited Time Offer', es: 'Oferta por tiempo limitado' },
  },
};

/** Plans above the given one, cheapest first (Basic -> [pro, premium]). */
export const upgradeTargets = (current?: string | null): Array<'pro' | 'premium'> => {
  const order = ['basic', 'pro', 'premium'];
  const idx = current ? order.indexOf(current) : -1;
  return (['pro', 'premium'] as const).filter((p) => order.indexOf(p) > idx);
};

export interface PlanOption {
  code: PlanCode;
  name: string;
  /** Monthly charge in cents. */
  price: number;
  /** Lead credits granted at the start of every billing cycle. */
  leads: number;
  /** Minimum commitment in months (1 = cancel anytime). */
  termMonths: number;
}

/**
 * The three membership plans as a ready-to-render list, DERIVED from the maps
 * above — never a second hand-typed copy of the pricing. Used by the floating
 * "Start Now" plan picker.
 */
export const planOptions = (lang: Lang): PlanOption[] =>
  PLAN_CODES.map((code) => ({
    code,
    name: planLabel(code, lang),
    price: PLAN_PRICES[code],
    leads: PLAN_INCLUDED_LEADS[code],
    termMonths: planTermMonths(code),
  }));


/**
 * ───────────────────────────────────────────────────────────────────────────
 * PLAN ALLOWANCE RECONCILIATION — stops "20 vs 25" drift from EVER shipping.
 * ───────────────────────────────────────────────────────────────────────────
 * The plan rows in `mkt_plans` carry two things that can disagree:
 *   included_leads  -> a NUMBER (authoritative-ish, editable in the database)
 *   features_en/es  -> FREE TEXT bullets that may also state a lead count
 *
 * Free text cannot be trusted: the Premium bullet said "20 included leads"
 * months after the allowance was raised to 25, so the Pricing page advertised
 * one number while the webhook granted another.
 *
 * From here on, PLAN_INCLUDED_LEADS (this file) is the ONE authority for what
 * a customer is told, and every customer-facing surface pipes plan copy
 * through `reconcileFeatureLine()`. Any bullet that states a lead count is
 * rewritten to the authoritative number at render time, so even if a stale row
 * is restored from a backup, or an admin retypes a bullet by hand, the site
 * physically cannot display a count that differs from what is granted.
 */

/** Authoritative monthly credits for a plan code (0 for unknown codes). */
export const planLeads = (code?: string | null): number =>
  (code && PLAN_INCLUDED_LEADS[code]) || 0;

/**
 * Matches any "<N> included leads ..." / "<N> leads incluidos ..." bullet in
 * either language, capturing the number so it can be replaced.
 */
const LEAD_COUNT_BULLET = /^(\s*)(\d+)(\s+)(included\s+leads|leads\s+incluidos)/i;

/**
 * Rewrite a single stored feature bullet so any stated lead count matches the
 * authoritative allowance. Bullets that do not mention a lead count are
 * returned untouched.
 */
export const reconcileFeatureLine = (code: string | null | undefined, line: string): string => {
  const n = planLeads(code);
  if (!n) return line;
  return line.replace(LEAD_COUNT_BULLET, (_m, pre, _old, gap, label) => `${pre}${n}${gap}${label}`);
};

/**
 * Customer-facing feature bullets for a plan, reconciled against
 * PLAN_INCLUDED_LEADS. Always use this instead of rendering features_en /
 * features_es straight out of the database.
 */
export const planFeatureLines = (
  code: string | null | undefined,
  features: string[] | null | undefined
): string[] => (features || []).map((f) => reconcileFeatureLine(code, f));

/**
 * Drift detector for the admin console: returns the bullets whose stated lead
 * count does not match the authoritative allowance, plus the mismatch between
 * a database `included_leads` value and PLAN_INCLUDED_LEADS. An empty result
 * means the stored copy and the granted credits agree exactly.
 */
export const planCopyDrift = (
  code: string,
  dbIncludedLeads: number | null | undefined,
  features: string[] | null | undefined
): string[] => {
  const n = planLeads(code);
  const out: string[] = [];
  if (n && dbIncludedLeads != null && dbIncludedLeads !== n) {
    out.push(`included_leads = ${dbIncludedLeads}, expected ${n}`);
  }
  (features || []).forEach((f) => {
    const m = f.match(LEAD_COUNT_BULLET);
    if (m && Number(m[2]) !== n) out.push(`"${f}" states ${m[2]}, expected ${n}`);
  });
  return out;
};




export type UserType = 'homeowner' | 'gc' | 'subcontractor';

export type Lang = 'en' | 'es';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * SERVICE GROUPS — GIGGONE is a complete homeowner-services marketplace, not
 * only a construction board. Every marketplace category belongs to exactly one
 * group; the home page category rail, the marketplace filter and the sign-up
 * trade pickers all group by this list, so a new category only needs ONE row
 * in TRADES below. Analysis: docs/HOME_SERVICES_EXPANSION_20261011.md.
 * ───────────────────────────────────────────────────────────────────────────
 */
export type ServiceGroup = 'home' | 'outdoor' | 'mechanical' | 'property' | 'safety' | 'restoration' | 'construction';

export const SERVICE_GROUPS: { key: ServiceGroup; en: string; es: string }[] = [
  { key: 'home', en: 'Home Services', es: 'Servicios para el Hogar' },
  { key: 'outdoor', en: 'Outdoor Services', es: 'Servicios Exteriores' },
  { key: 'mechanical', en: 'Mechanical Services', es: 'Servicios Mecánicos' },
  { key: 'property', en: 'Property Services', es: 'Servicios de Propiedades' },
  { key: 'safety', en: 'Safety & Security', es: 'Seguridad' },
  { key: 'restoration', en: 'Restoration', es: 'Restauración' },
  { key: 'construction', en: 'Construction & Remodeling', es: 'Construcción y Remodelación' },
];

export const serviceGroupLabel = (key: ServiceGroup, lang: Lang): string => {
  const g = SERVICE_GROUPS.find((x) => x.key === key);
  return g ? (lang === 'es' ? g.es : g.en) : key;
};

/**
 * How often the SAME homeowner typically needs the service again.
 * 'project' = one-off job. Anything else is a repeat/recurring service and is
 * badged "Recurring" in the UI — these categories create repeat homeowners and
 * repeat contractor lead purchases.
 */
export type RepeatCadence = 'weekly' | 'monthly' | 'quarterly' | 'seasonal' | 'annual' | 'project';

export const CADENCE_LABELS: Record<RepeatCadence, { en: string; es: string }> = {
  weekly: { en: 'Weekly service', es: 'Servicio semanal' },
  monthly: { en: 'Monthly service', es: 'Servicio mensual' },
  quarterly: { en: 'Quarterly service', es: 'Servicio trimestral' },
  seasonal: { en: 'Seasonal service', es: 'Servicio de temporada' },
  annual: { en: 'Yearly service', es: 'Servicio anual' },
  project: { en: 'One-time project', es: 'Proyecto único' },
};

/** Trade / category slugs shared by projects, work orders, filters and SEO URLs. */
export interface Trade {
  slug: string;
  en: string;
  es: string;
  /** es slug used in Spanish SEO urls */
  esSlug: string;
  homeowner: boolean;
  gc: boolean;
  group: ServiceGroup;
  cadence: RepeatCadence;
}

type T = Trade;
const tr = (slug: string, en: string, es: string, esSlug: string, group: ServiceGroup, cadence: RepeatCadence, homeowner = true, gc = false): T =>
  ({ slug, en, es, esSlug, group, cadence, homeowner, gc });

/**
 * ORDER MATTERS: the original 16 construction trades stay first (the footer
 * links TRADES.slice(0, 8) and existing listings / saved searches use these
 * slugs). New categories append; they NEVER rename an existing slug.
 */
export const TRADES: Trade[] = [
  tr('roofing', 'Roofing', 'Techado', 'techado', 'construction', 'project', true, true),
  tr('plumbing', 'Plumbing', 'Plomería', 'plomeria', 'mechanical', 'annual', true, true),
  tr('electrical', 'Electrical', 'Electricidad', 'electricidad', 'mechanical', 'project', true, true),
  tr('hvac', 'HVAC', 'Aire y Calefacción', 'aire-acondicionado', 'mechanical', 'seasonal', true, true),
  tr('flooring', 'Flooring', 'Pisos', 'pisos', 'construction', 'project', true, true),
  tr('painting', 'Painting', 'Pintura', 'pintura', 'construction', 'project', true, true),
  tr('solar', 'Solar', 'Solar', 'solar', 'mechanical', 'project'),
  tr('concrete', 'Concrete', 'Concreto', 'concreto', 'construction', 'project', true, true),
  tr('landscaping', 'Landscaping', 'Jardinería', 'jardineria', 'outdoor', 'project'),
  tr('windows', 'Windows', 'Ventanas', 'ventanas', 'construction', 'project'),
  tr('doors', 'Doors', 'Puertas', 'puertas', 'construction', 'project'),
  tr('kitchen-remodel', 'Kitchen Remodel', 'Remodelación de Cocina', 'remodelacion-cocina', 'construction', 'project'),
  tr('bathroom-remodel', 'Bathroom Remodel', 'Remodelación de Baño', 'remodelacion-bano', 'construction', 'project'),
  tr('general-construction', 'General Construction', 'Construcción General', 'construccion-general', 'construction', 'project', true, true),
  tr('framing', 'Framing', 'Estructura / Framing', 'estructura', 'construction', 'project', false, true),
  tr('drywall', 'Drywall', 'Tablaroca', 'tablaroca', 'construction', 'project', false, true),

  // ── HOME SERVICES ───────────────────────────────────────────────────────
  tr('handyman', 'Handyman', 'Handyman / Reparaciones', 'handyman', 'home', 'quarterly', true, true),
  tr('house-cleaning', 'House Cleaning', 'Limpieza de Casas', 'limpieza-casas', 'home', 'weekly'),
  tr('deep-cleaning', 'Deep Cleaning', 'Limpieza Profunda', 'limpieza-profunda', 'home', 'quarterly'),
  tr('move-out-cleaning', 'Move-Out Cleaning', 'Limpieza de Mudanza', 'limpieza-mudanza', 'home', 'project', true, true),
  tr('window-cleaning', 'Window Cleaning', 'Limpieza de Ventanas', 'limpieza-ventanas', 'home', 'quarterly'),
  tr('gutter-cleaning', 'Gutter Cleaning', 'Limpieza de Canaletas', 'limpieza-canaletas', 'home', 'seasonal'),
  tr('pressure-washing', 'Pressure Washing', 'Lavado a Presión', 'lavado-presion', 'home', 'annual', true, true),
  tr('carpet-cleaning', 'Carpet Cleaning', 'Limpieza de Alfombras', 'limpieza-alfombras', 'home', 'annual'),
  tr('air-duct-cleaning', 'Air Duct Cleaning', 'Limpieza de Ductos', 'limpieza-ductos', 'home', 'annual'),
  tr('dryer-vent-cleaning', 'Dryer Vent Cleaning', 'Limpieza de Ducto de Secadora', 'limpieza-secadora', 'home', 'annual'),
  tr('junk-removal', 'Junk Removal', 'Retiro de Basura', 'retiro-basura', 'home', 'project', true, true),
  tr('furniture-assembly', 'Furniture Assembly', 'Armado de Muebles', 'armado-muebles', 'home', 'project'),
  tr('tv-mounting', 'TV Mounting', 'Instalación de TV', 'instalacion-tv', 'home', 'project'),
  tr('pool-cleaning', 'Pool Cleaning', 'Limpieza de Albercas', 'limpieza-albercas', 'home', 'weekly'),
  tr('pool-repair', 'Pool Repair', 'Reparación de Albercas', 'reparacion-albercas', 'home', 'annual'),
  tr('pest-control', 'Pest Control', 'Control de Plagas', 'control-plagas', 'home', 'quarterly'),

  // ── OUTDOOR SERVICES ────────────────────────────────────────────────────
  tr('lawn-care', 'Lawn Care', 'Cuidado del Césped', 'cuidado-cesped', 'outdoor', 'weekly'),
  tr('tree-trimming', 'Tree Trimming', 'Poda de Árboles', 'poda-arboles', 'outdoor', 'annual'),
  tr('stump-removal', 'Stump Removal', 'Remoción de Tocones', 'remocion-tocones', 'outdoor', 'project'),
  tr('irrigation', 'Irrigation', 'Riego', 'riego', 'outdoor', 'seasonal', true, true),
  tr('artificial-turf', 'Artificial Turf', 'Césped Artificial', 'cesped-artificial', 'outdoor', 'project'),
  tr('fence-repair', 'Fence Repair', 'Reparación de Cercas', 'reparacion-cercas', 'outdoor', 'project', true, true),
  tr('gate-repair', 'Gate Repair', 'Reparación de Portones', 'reparacion-portones', 'outdoor', 'project'),

  // ── MECHANICAL SERVICES ─────────────────────────────────────────────────
  tr('appliance-repair', 'Appliance Repair', 'Reparación de Electrodomésticos', 'reparacion-electrodomesticos', 'mechanical', 'project'),
  tr('water-heater-repair', 'Water Heater Repair', 'Reparación de Calentadores', 'reparacion-calentadores', 'mechanical', 'project', true, true),
  tr('garage-door-repair', 'Garage Door Repair', 'Reparación de Puertas de Garaje', 'reparacion-puertas-garaje', 'mechanical', 'project'),
  tr('ev-charger-installation', 'EV Charger Installation', 'Instalación de Cargadores EV', 'cargadores-ev', 'mechanical', 'project', true, true),

  // ── PROPERTY SERVICES (landlords, property managers, HOAs) ─────────────
  tr('property-maintenance', 'Property Maintenance', 'Mantenimiento de Propiedades', 'mantenimiento-propiedades', 'property', 'monthly', true, true),
  tr('rental-turnover', 'Rental Turnover Services', 'Preparación de Rentas', 'preparacion-rentas', 'property', 'project', true, true),
  tr('hoa-maintenance', 'HOA Maintenance', 'Mantenimiento de HOA', 'mantenimiento-hoa', 'property', 'monthly', true, true),
  tr('property-inspections', 'Property Inspections', 'Inspección de Propiedades', 'inspeccion-propiedades', 'property', 'annual', true, true),

  // ── SAFETY & SECURITY ───────────────────────────────────────────────────
  tr('security-cameras', 'Security Cameras', 'Cámaras de Seguridad', 'camaras-seguridad', 'safety', 'project', true, true),
  tr('alarm-systems', 'Alarm Systems', 'Sistemas de Alarma', 'sistemas-alarma', 'safety', 'monthly', true, true),
  tr('access-control', 'Access Control', 'Control de Acceso', 'control-acceso', 'safety', 'project', true, true),
  tr('smart-home', 'Smart Home Installation', 'Instalación de Casa Inteligente', 'casa-inteligente', 'safety', 'project'),

  // ── RESTORATION ─────────────────────────────────────────────────────────
  tr('water-damage', 'Water Damage', 'Daños por Agua', 'danos-agua', 'restoration', 'project', true, true),
  tr('mold-remediation', 'Mold Remediation', 'Remediación de Moho', 'remediacion-moho', 'restoration', 'project', true, true),
  tr('fire-damage', 'Fire Damage Restoration', 'Restauración por Incendio', 'restauracion-incendio', 'restoration', 'project', true, true),
];

/** True when the category is a repeat / recurring service. */
export const isRecurringTrade = (slug: string): boolean => {
  const t = TRADES.find((x) => x.slug === slug);
  return !!t && t.cadence !== 'project';
};

/** Trades in display order, grouped by SERVICE_GROUPS (empty groups dropped). */
export const tradesByGroup = (list: Trade[] = TRADES): { group: (typeof SERVICE_GROUPS)[number]; trades: Trade[] }[] =>
  SERVICE_GROUPS.map((group) => ({ group, trades: list.filter((t) => t.group === group.key) })).filter((g) => g.trades.length > 0);

/** Spanish localized job slug: roofing-fontana-ca-1001 -> techado-fontana-ca-1001 */
export const localizedSlug = (slug: string, trade: string, lang: Lang): string => {
  if (lang !== 'es') return slug;
  const t = TRADES.find((x) => x.slug === trade);
  if (!t) return slug;
  return slug.startsWith(`${t.slug}-`) ? `${t.esSlug}-${slug.slice(t.slug.length + 1)}` : slug;
};

/**
 * Map a localized slug back to the canonical English slug stored in the
 * database. Longest Spanish prefix wins, so "reparacion-puertas-garaje-…"
 * can never be mis-read as "puertas-…" (or any future overlapping prefix).
 */
const TRADES_BY_ES_PREFIX = [...TRADES].sort((a, b) => b.esSlug.length - a.esSlug.length);
export const canonicalSlug = (slug: string): string => {
  const t = TRADES_BY_ES_PREFIX.find((x) => slug.startsWith(`${x.esSlug}-`));
  if (!t) return slug;
  return `${t.slug}-${slug.slice(t.esSlug.length + 1)}`;
};

export const tradeLabel = (slug: string, lang: Lang): string => {
  const t = TRADES.find((x) => x.slug === slug);
  if (!t) return slug;
  return lang === 'es' ? t.es : t.en;
};

export const homeownerCategories = () => TRADES.filter((t) => t.homeowner);
export const gcTrades = () => TRADES.filter((t) => t.gc);

/**
 * ───────────────────────────────────────────────────────────────────────────
 * BUDGET BANDS — the single source of truth for budget display AND filtering.
 * ───────────────────────────────────────────────────────────────────────────
 * Every band carries BOTH the human label (EN/ES, stored in
 * mkt_listings.budget_range for display) and the STRUCTURED numbers written to
 * mkt_listings.budget_min / budget_max, with budget_currency = 'USD'.
 *
 * Why structured columns: filtering and sorting must never be derived from
 * display text. The old "$5,000 - $15,000" -> digits parse produced 500001 and
 * made the minimum-budget filter a no-op. Numbers are now saved at intake and
 * the free-text parse is only a legacy fallback for rows posted before this
 * change (see listingBudgetCeiling in src/lib/marketplace.ts).
 *
 * `max: null` = open-ended top band ("$100,000+").
 * "Not sure" is represented by the EMPTY selection: no label, no numbers, and
 * NO invented amount. A listing with no budget on file is never filtered out.
 */
export interface BudgetBand {
  /** English label stored in budget_range (canonical, used by SEO + filters). */
  en: string;
  es: string;
  /** Inclusive floor in whole dollars. */
  min: number;
  /** Inclusive ceiling in whole dollars, or null for an open-ended band. */
  max: number | null;
}

export const BUDGET_BANDS: BudgetBand[] = [
  { en: 'Under $5,000', es: 'Menos de $5,000', min: 0, max: 5000 },
  { en: '$5,000 - $15,000', es: '$5,000 - $15,000', min: 5000, max: 15000 },
  { en: '$15,000 - $30,000', es: '$15,000 - $30,000', min: 15000, max: 30000 },
  { en: '$30,000 - $60,000', es: '$30,000 - $60,000', min: 30000, max: 60000 },
  { en: '$60,000 - $100,000', es: '$60,000 - $100,000', min: 60000, max: 100000 },
  { en: '$100,000+', es: 'Más de $100,000', min: 100000, max: null },
];

/** Default currency for the structured budget columns. */
export const BUDGET_CURRENCY = 'USD';

/** Find a band from either its English or Spanish label. */
export const budgetBandFor = (label?: string | null): BudgetBand | null => {
  const raw = (label || '').trim();
  if (!raw) return null;
  return BUDGET_BANDS.find((b) => b.en === raw || b.es === raw) || null;
};

/**
 * Structured columns for a chosen label. Returns nulls for "Not sure" /
 * no selection so the database keeps an honest "unknown", never a guess.
 */
export const budgetColumnsFor = (label?: string | null) => {
  const band = budgetBandFor(label);
  return {
    budget_range: band ? band.en : null,
    budget_min: band ? band.min : null,
    budget_max: band ? band.max : null,
    budget_currency: BUDGET_CURRENCY,
  };
};

/** Guard used by the posting form: min must never exceed max. */
export const budgetRangeIsValid = (min: number | null, max: number | null): boolean => {
  if (min === null || max === null) return true; // open-ended / unknown is fine
  return min <= max;
};

/** Labels in the active language (posting form select). */
export const budgetLabels = (lang: Lang): string[] =>
  BUDGET_BANDS.map((b) => (lang === 'es' ? b.es : b.en));

/** Back-compat exports — derived, never a second hand-typed copy. */
export const BUDGET_RANGES = BUDGET_BANDS.map((b) => b.en);
export const BUDGET_RANGES_ES = BUDGET_BANDS.map((b) => b.es);
/**
 * NATIONWIDE FOOTER MARKETS — the state markets highlighted under
 * "Browse by Location". GIGGONE operates across the United States; this is a
 * short, high-volume shortlist, NOT the coverage limit. California links to its
 * dedicated county/city hub; every other state links to its state landing page
 * (/states/xx, see src/data/seo/states.ts), and the /states index covers all
 * 50 states + DC.
 */
export const FEATURED_MARKETS: { code: string; name: string; hub?: string }[] = [
  { code: 'CA', name: 'California', hub: '/california' },
  { code: 'TX', name: 'Texas' },
  { code: 'FL', name: 'Florida' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'NV', name: 'Nevada' },
];


export const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

/**
 * ───────────────────────────────────────────────────────────────────────────
 * COVER IMAGE LIBRARY — one entry for EVERY trade in TRADES.
 * ───────────────────────────────────────────────────────────────────────────
 * TRADE_IMAGES is the single source of truth for
 *   a) the trade photos on the home page trade strip (FEATURED_TRADE_SLUGS), and
 *   b) the trade-specific marketplace cover used when a project has no
 *      uploaded photo (see src/lib/coverImage.ts).
 *
 * RULE: every slug in TRADES must have a key here. A project must NEVER fall
 * through to a blank/gray tile, so `assertTradeImageCoverage()` below is used
 * by tradeCoverImage() to keep this honest if a trade is added later.
 *
 * All files are pre-compressed WebP (~40-290 KB) at 1200px wide so a card grid
 * stays cheap on mobile; they are served from the CDN with long cache headers.
 */
export const TRADE_IMAGES: Record<string, string> = {
  // ── featured six (also used by the home page trade strip) ──────────────
  roofing: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601932679_5ad94eef.webp',
  plumbing: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601932529_b0a6aff8.webp',
  electrical: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601932540_9ea9a6d5.webp',
  hvac: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601932578_c5d0c0ca.webp',
  concrete: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601932739_4d717c52.webp',
  'kitchen-remodel': 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601932682_cd1dbcd1.webp',
  // ── remaining marketplace trades ───────────────────────────────────────
  painting: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601405216_602aac3b.webp',
  flooring: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601405107_b8a230cb.webp',
  'bathroom-remodel': 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601405019_dd60fb7f.webp',
  landscaping: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601405145_b0521d6d.webp',
  solar: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601405090_8235e80e.webp',
  windows: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601405332_53f039ae.webp',
  doors: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601404854_2f284fd0.webp',
  'general-construction': 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601404899_5e69a5f9.webp',
  framing: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601404835_9998792f.webp',
  drywall: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601404806_5372cbae.webp',
  // ── home-services expansion (2026-10-11) — one photo per anchor service;
  //    sibling services share an anchor through TRADE_IMAGE_ALIASES below. ──
  'house-cleaning': 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1791733709645_6590b995.webp',
  'pool-cleaning': 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1791733709533_d158e4cc.webp',
  handyman: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1791733709530_3716d91e.webp',
  'junk-removal': 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1791733709721_03d7d242.webp',
  'pressure-washing': 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1791733709897_18bd3cc8.webp',
  'lawn-care': 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1791733709854_e3ef0796.webp',
  'tree-trimming': 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1791733709614_92ded62f.webp',
  'security-cameras': 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1791733709826_9f899b09.webp',
  'water-damage': 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1791733709464_28f91b54.webp',
  'appliance-repair': 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1791733709267_a8d9f373.webp',
  'ev-charger-installation': 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1791733709699_6379705d.webp',
  'pest-control': 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1791733709340_e2e05371.webp',
  'garage-door-repair': 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1791733709337_dd8765b5.webp',
  'window-cleaning': 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1791733709360_d7bb5db2.webp',
};


/**
 * Cover-image aliases for SEO trade slugs, legacy/synonym categories and the
 * home-service categories that share an anchor photo. Keeps a hub page or an
 * imported record from ever rendering a blank cover.
 */
export const TRADE_IMAGE_ALIASES: Record<string, string> = {
  'windows-doors': 'windows',
  window: 'windows',
  door: 'doors',
  paint: 'painting',
  floors: 'flooring',
  tile: 'flooring',
  roof: 'roofing',
  're-roof': 'roofing',
  hvac_install: 'hvac',
  'air-conditioning': 'hvac',
  kitchen: 'kitchen-remodel',
  bathroom: 'bathroom-remodel',
  bath: 'bathroom-remodel',
  remodel: 'general-construction',
  construction: 'general-construction',
  'residential-construction': 'general-construction',
  'commercial-construction': 'general-construction',
  'general-contracting': 'general-construction',
  demolition: 'general-construction',
  excavation: 'concrete',
  masonry: 'concrete',
  stucco: 'painting',
  foundation: 'concrete',
  hardscape: 'concrete',
  pool: 'pool-cleaning',
  pools: 'pool-cleaning',
  fencing: 'landscaping',
  fence: 'landscaping',
  turf: 'lawn-care',
  carpentry: 'framing',
  'rough-carpentry': 'framing',
  insulation: 'drywall',
  cabinets: 'kitchen-remodel',
  electrician: 'electrical',
  plumber: 'plumbing',
  gutters: 'roofing',
  'garage-doors': 'garage-door-repair',
  'security-systems': 'security-cameras',
  'low-voltage': 'security-cameras',
  // ── home-services siblings → anchor photo ─────────────────────────────
  'deep-cleaning': 'house-cleaning',
  'move-out-cleaning': 'house-cleaning',
  'carpet-cleaning': 'house-cleaning',
  'air-duct-cleaning': 'hvac',
  'dryer-vent-cleaning': 'appliance-repair',
  'chimney-sweep': 'roofing',
  'gutter-cleaning': 'roofing',
  'solar-panel-cleaning': 'solar',
  'furniture-assembly': 'handyman',
  'tv-mounting': 'handyman',
  locksmith: 'doors',
  'pool-repair': 'pool-cleaning',
  'stump-removal': 'tree-trimming',
  irrigation: 'lawn-care',
  'artificial-turf': 'lawn-care',
  'fence-repair': 'landscaping',
  'gate-repair': 'landscaping',
  'water-heater-repair': 'plumbing',
  'property-maintenance': 'handyman',
  'rental-turnover': 'house-cleaning',
  'hoa-maintenance': 'lawn-care',
  'property-inspections': 'general-construction',
  'alarm-systems': 'security-cameras',
  'access-control': 'security-cameras',
  'smart-home': 'security-cameras',
  'mold-remediation': 'water-damage',
  'fire-damage': 'water-damage',
};

/**
 * Resolve the branded cover photo for a trade/category slug.
 * Order: exact trade -> alias -> null (caller falls back to the marketplace
 * cover). Never returns an empty string.
 */
export const tradeCoverImage = (slug?: string | null): string | null => {
  if (!slug) return null;
  const key = slug.trim().toLowerCase();
  const direct = TRADE_IMAGES[key];
  if (direct) return direct;
  const aliased = TRADE_IMAGE_ALIASES[key];
  return (aliased && TRADE_IMAGES[aliased]) || null;
};

/** Trades that have no cover photo yet — must always be empty in production. */
export const tradesMissingCoverImage = (): string[] =>
  TRADES.filter((tr) => !tradeCoverImage(tr.slug)).map((tr) => tr.slug);

/** The six trades featured in the "Every trade on the jobsite" section, in order. */
export const FEATURED_TRADE_SLUGS = [
  'roofing',
  'plumbing',
  'electrical',
  'hvac',
  'concrete',
  'kitchen-remodel',
];

export const IMAGES = {
  hero: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1788910228966_072120a3.jpg',
};


/**
 * HERO MOTION SEQUENCE — the cinematic cross-fading backdrop behind the home
 * page headline. Each frame is a real photo of the actual marketplace journey:
 * homeowner documenting a project, a crew working, the finished remodel, the
 * handshake at completion.
 *
 * WHY A PHOTO SEQUENCE AND NOT AN AUTOPLAY <video>:
 * an autoplaying background video costs 3-8 MB on first paint, is the single
 * biggest LCP regression available, is throttled or blocked outright on iOS
 * Low Power Mode and on metered mobile connections, and cannot be captioned.
 * These four WebP frames total ~390 KB, animate with GPU-only transforms, and
 * respect prefers-reduced-motion. If a produced brand film is ever licensed,
 * set HERO_VIDEO_URL below and HeroVideo renders it as the backdrop instead —
 * no other code has to change.
 */
export const HERO_SEQUENCE: { src: string; alt_en: string; alt_es: string }[] = [
  {
    src: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789514671647_1869f8a1.webp',
    alt_en: 'Homeowner photographing a damaged kitchen cabinet to post a project',
    alt_es: 'Dueña de casa fotografiando un gabinete dañado para publicar un proyecto',
  },
  {
    src: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789514671679_39e87fe1.webp',
    alt_en: 'Roofing crew installing shingles on a residential roof',
    alt_es: 'Cuadrilla de techado instalando tejas en un techo residencial',
  },
  {
    src: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789514671650_b62cadf2.webp',
    alt_en: 'Completed modern kitchen remodel with quartz island',
    alt_es: 'Remodelación de cocina moderna terminada con isla de cuarzo',
  },
  {
    src: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789514671696_721ec938.webp',
    alt_en: 'Homeowner and contractor shaking hands after a completed project',
    alt_es: 'Dueño de casa y contratista dándose la mano al terminar el proyecto',
  },
];

/**
 * Optional produced brand film. Leave as an empty string until a real MP4 is
 * licensed and hosted — HeroVideo falls back to HERO_SEQUENCE automatically.
 * Never point this at a third-party/hotlinked video: a cross-origin media URL
 * on the landing page is a reputation signal security vendors score against.
 */
export const HERO_VIDEO_URL = '';

/**
 * Trade strip accent colors. Keyed by trade slug so the horizontal category
 * rail, the trade hub pages and any future filter chips all tint identically.
 * Trades without their own tint inherit their SERVICE GROUP tint (below), so a
 * new home-service category is never a grey tile.
 */
export const TRADE_ACCENTS: Record<string, { bg: string; fg: string; ring: string }> = {
  roofing: { bg: 'bg-orange-50', fg: 'text-orange-600', ring: 'ring-orange-200' },
  plumbing: { bg: 'bg-sky-50', fg: 'text-sky-600', ring: 'ring-sky-200' },
  electrical: { bg: 'bg-amber-50', fg: 'text-amber-600', ring: 'ring-amber-200' },
  concrete: { bg: 'bg-slate-100', fg: 'text-slate-600', ring: 'ring-slate-300' },
  painting: { bg: 'bg-violet-50', fg: 'text-violet-600', ring: 'ring-violet-200' },
  flooring: { bg: 'bg-rose-50', fg: 'text-rose-600', ring: 'ring-rose-200' },
  hvac: { bg: 'bg-cyan-50', fg: 'text-cyan-600', ring: 'ring-cyan-200' },
  landscaping: { bg: 'bg-emerald-50', fg: 'text-emerald-600', ring: 'ring-emerald-200' },
  'kitchen-remodel': { bg: 'bg-indigo-50', fg: 'text-indigo-600', ring: 'ring-indigo-200' },
  'bathroom-remodel': { bg: 'bg-teal-50', fg: 'text-teal-600', ring: 'ring-teal-200' },
  solar: { bg: 'bg-yellow-50', fg: 'text-yellow-600', ring: 'ring-yellow-200' },
  windows: { bg: 'bg-blue-50', fg: 'text-blue-600', ring: 'ring-blue-200' },
  doors: { bg: 'bg-stone-100', fg: 'text-stone-600', ring: 'ring-stone-300' },
  framing: { bg: 'bg-lime-50', fg: 'text-lime-700', ring: 'ring-lime-200' },
  drywall: { bg: 'bg-neutral-100', fg: 'text-neutral-600', ring: 'ring-neutral-300' },
  'general-construction': { bg: 'bg-slate-100', fg: 'text-slate-700', ring: 'ring-slate-300' },
};

/** Fallback tint per service group. */
export const GROUP_ACCENTS: Record<ServiceGroup, { bg: string; fg: string; ring: string }> = {
  home: { bg: 'bg-sky-50', fg: 'text-sky-600', ring: 'ring-sky-200' },
  outdoor: { bg: 'bg-emerald-50', fg: 'text-emerald-600', ring: 'ring-emerald-200' },
  mechanical: { bg: 'bg-amber-50', fg: 'text-amber-600', ring: 'ring-amber-200' },
  property: { bg: 'bg-indigo-50', fg: 'text-indigo-600', ring: 'ring-indigo-200' },
  safety: { bg: 'bg-violet-50', fg: 'text-violet-600', ring: 'ring-violet-200' },
  restoration: { bg: 'bg-rose-50', fg: 'text-rose-600', ring: 'ring-rose-200' },
  construction: { bg: 'bg-orange-50', fg: 'text-orange-600', ring: 'ring-orange-200' },
};

export const tradeAccent = (slug: string) => {
  if (TRADE_ACCENTS[slug]) return TRADE_ACCENTS[slug];
  const tr = TRADES.find((x) => x.slug === slug);
  return tr ? GROUP_ACCENTS[tr.group] : { bg: 'bg-slate-100', fg: 'text-slate-600', ring: 'ring-slate-300' };
};

/**
 * TYPICAL project duration by trade, in working days [min, max].
 *
 * These are EDITORIAL PLANNING ESTIMATES for a standard single-family job, not
 * a promise and not derived from platform data. Every surface that renders them
 * must label them as typical (see `jobs.typicalTimeline`) so nobody mistakes an
 * estimate for a committed schedule. Scope, permits and inspections move real
 * timelines, which is why no per-listing duration is ever invented.
 */
export const TYPICAL_DURATION_DAYS: Record<string, [number, number]> = {
  roofing: [2, 5],
  plumbing: [1, 3],
  electrical: [1, 4],
  hvac: [1, 3],
  flooring: [2, 6],
  painting: [2, 5],
  solar: [2, 4],
  concrete: [3, 7],
  landscaping: [3, 10],
  windows: [1, 3],
  doors: [1, 2],
  'kitchen-remodel': [15, 45],
  'bathroom-remodel': [10, 25],
  'general-construction': [10, 60],
  framing: [5, 20],
  drywall: [3, 8],
  // ── home & recurring services (single visit unless noted) ──────────────
  handyman: [1, 2],
  'house-cleaning': [1, 1],
  'deep-cleaning': [1, 2],
  'move-out-cleaning': [1, 2],
  'window-cleaning': [1, 1],
  'gutter-cleaning': [1, 1],
  'pressure-washing': [1, 1],
  'carpet-cleaning': [1, 1],
  'air-duct-cleaning': [1, 1],
  'junk-removal': [1, 1],
  'furniture-assembly': [1, 1],
  'tv-mounting': [1, 1],
  'pool-cleaning': [1, 1],
  'pool-repair': [1, 3],
  'pest-control': [1, 1],
  'lawn-care': [1, 1],
  'tree-trimming': [1, 2],
  'stump-removal': [1, 1],
  irrigation: [1, 3],
  'artificial-turf': [2, 5],
  'fence-repair': [1, 3],
  'gate-repair': [1, 2],
  'appliance-repair': [1, 2],
  'water-heater-repair': [1, 1],
  'garage-door-repair': [1, 1],
  'ev-charger-installation': [1, 2],
  'property-maintenance': [1, 3],
  'rental-turnover': [2, 7],
  'hoa-maintenance': [1, 5],
  'property-inspections': [1, 1],
  'security-cameras': [1, 2],
  'alarm-systems': [1, 2],
  'access-control': [1, 3],
  'smart-home': [1, 2],
  'water-damage': [3, 10],
  'mold-remediation': [2, 7],
  'fire-damage': [7, 30],
};

/** Typical duration label, e.g. "2-5 days" or "1 day". Returns null when unknown. */
export const typicalDurationLabel = (trade: string, lang: Lang): string | null => {
  const range = TYPICAL_DURATION_DAYS[trade];
  if (!range) return null;
  const [lo, hi] = range;
  if (lo === hi) return lang === 'es' ? `${lo} día${lo === 1 ? '' : 's'}` : `${lo} day${lo === 1 ? '' : 's'}`;
  return lang === 'es' ? `${lo}-${hi} días` : `${lo}-${hi} days`;
};



export const ADMIN_EMAIL = 'raulrodartejr@skyisthelimitshop.com';

/** Cents -> display string with thousands separators, e.g. 598800 -> "$5,988". */
export const money = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  })}`;


/** Add-on modules — intentionally kept OUT of primary navigation. */
export const ADDONS = [
  { code: 'crm', price: 4900 },
  { code: 'crew', price: 3900 },
  { code: 'chat', price: 2900 },
  { code: 'logs', price: 2900 },
];
